import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { AppConstants } from '../../app.constants';
import { OrgSelectorService } from '../../core/services/org-selector.service';
import { AuthenticationsService } from '../../modules/authentications/authentications.service';
import { AuthenticationLoginResponseDto } from '../../modules/authentications/dtos/authentication-login-response';
import { OrganizationDto } from '../../modules/organizations/dtos/organization';
import { OrganizationMenuSummaryDto } from '../../modules/organizations/dtos/organization-menu-summary';
import { OrganizationsService } from '../../modules/organizations/organizations.service';
import { fetchGroupsStart } from '../../sanus/groups/state/groups.actions';
import { AppState } from '../../store/app.state';
import { PaginationArgs } from '../components/pagination/models/pagination-args.model';
import { SortingType } from '../enums/sorting-type.enum';
import { MessageResponse } from '../models/message-response.model';
import { RouteUtils } from '../utils/route/route.util';

@Component({
	selector: 'app-header',
	styleUrls: ['./header.component.scss'],
	templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit, OnDestroy {
	@Output() action: EventEmitter<void> = new EventEmitter();
	@Output() config: EventEmitter<void> = new EventEmitter();
	@Output() home: EventEmitter<void> = new EventEmitter();
	@Output() logout: EventEmitter<void> = new EventEmitter();
	@Output() nav: EventEmitter<string> = new EventEmitter();
	@Output() notifications: EventEmitter<void> = new EventEmitter();
	@Input() notificationsCount: number | null = null;

	isFavoriteActionDisabled: boolean = false;

	limit: number = 5;

	organizationName: string = '';
	organizations: OrganizationMenuSummaryDto[] = [];
	searchForm: UntypedFormGroup;
	session: AuthenticationLoginResponseDto | null =
		this.authenticationsService.getSession();
	showDialog: boolean = false;
	showOrganizationFilter: boolean = false;
	showOrganizationList: boolean = false;
	userOrganizationId?: string;

	private readonly debounceTimeMs: number = AppConstants.debounce.time;

	private readonly searchOrganizationFilter = new Subject<string>();
	private searchSubscription$: Subscription = new Subscription();
	private toggleSubscription$: Subscription = new Subscription();
	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly authenticationsService: AuthenticationsService,
		private readonly fb: UntypedFormBuilder,
		private readonly organizationsService: OrganizationsService,
		private readonly orgSelection: OrgSelectorService,
		private readonly router: Router,
		private readonly store: Store<AppState>,
	) {
		this.searchForm = this.fb.group({
			term: [null],
		});
		this.userOrganizationId = this.session?.organization.id;
	}

	get organization(): string {
		return this.orgSelection.getOrganizationName() ?? this.organizationName;
	}

	formatName(): string {
		if (this.session?.name?.length) {
			return `${this.session.name[0] || ''}`;
		}

		return '';
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.searchSubscription$.unsubscribe();
		this.toggleSubscription$.unsubscribe();
	}

	ngOnInit(): void {
		this.searchOrganization(this.orgSelection.getOrganizationId());

		this.searchOrganizations();

		this.searchOrganizationFilter
			.pipe(debounceTime(this.debounceTimeMs))
			.subscribe((searchValue) => {
				this.searchOrganizations(searchValue);
			});
	}

	onCloseFilterList(): void {
		this.showOrganizationList = false;
		this.searchOrganizations();
	}

	onNavigate(path: string): void {
		this.showDialog = false;
		this.nav.emit(path);
	}

	async onOrganizationSelect(id: string, name: string): Promise<void> {
		this.orgSelection.setOrganizationId(id);
		this.orgSelection.setOrganizationName(name);

		if (RouteUtils.isOrganizationUuidPresent(this.router.url)) {
			const updatedRouteData = RouteUtils.updateOrganizationUuidInUrl(
				this.router.url,
				id,
			);
			await this.router.navigate(updatedRouteData.path, {
				queryParams: updatedRouteData.queryParams,
			});
		}

		if (
			this.router.url.includes('/inicio') ||
			this.router.url.includes('/movimentacoes')
		) {
			location.reload();
		}

		if (this.router.url.includes('/grupos')) {
			this.store.dispatch(fetchGroupsStart({ id: id }));
		}

		this.showOrganizationList = false;
	}

	onOrganizationsFilterOpen(): void {
		this.showOrganizationList = true;
	}

	onSearch(): void {
		const term = this.searchForm.get('term')!.value;

		if (term?.length) {
			this.searchOrganizationFilter.next(term);
		} else {
			this.searchOrganizations(term);
		}
	}

	onSearchKeyUp(event: KeyboardEvent): void {
		const term = this.searchForm.get('term')!.value;

		if (event.key === 'Enter' && term?.length) {
			this.searchOrganizations(term);
		} else {
			if (!term?.length) {
				this.searchOrganizations();
			}
		}
	}

	onShowNotifications(): void {
		this.notifications.emit();
	}

	onToggleFavorite(
		event: Event,
		organizationId: string,
		current: boolean,
	): void {
		event.stopPropagation();

		if (!this.isFavoriteActionDisabled) {
			this.isFavoriteActionDisabled = true;

			this.organizations = [];

			this.toggleSubscription$ = this.organizationsService
				.toggleFavorite(organizationId, current)
				.subscribe(async (response: MessageResponse) => {
					if (response?.type === 'success') {
						this.isFavoriteActionDisabled = false;
						this.searchOrganizations();
					}
				});
		}
	}

	onToggleListCount(): void {
		if (this.limit === 5) {
			this.limit = 8;
		} else {
			this.limit = 5;
		}

		this.searchOrganizations();
	}

	searchOrganization(id: string): void {
		const select = JSON.stringify({
			id: true,
			name_secondary: true,
		});

		const where = JSON.stringify({ id: id, active: true });
		const orderBy = JSON.stringify({ name_secondary: SortingType.ASCENDING });

		this.searchSubscription$ = this.organizationsService
			.findMany(
				new PaginationArgs({ limit: 1, orderBy, page: 1, select, where }),
			)
			.subscribe(async (response: any) => {
				this.organizationName =
					AppConstants.organizations.labelForAll ===
					this.orgSelection.getOrganizationName()
						? this.orgSelection.getOrganizationName()
						: response.result[0].name_secondary;
				this.orgSelection.setOrganizationName(this.organizationName);
			});
	}

	searchOrganizations(filter?: string): void {
		const select = {
			id: true,
			cnpj: true,
			isFavorite: true,
			name: true,
			name_secondary: true,
		};

		let where: string = JSON.stringify({ active: true });

		if (filter) {
			where = JSON.stringify({
				OR: [
					{ name_secondary: { contains: filter, mode: 'insensitive' } },
					{ name: { contains: filter, mode: 'insensitive' } },
					{ cnpj: { contains: filter, mode: 'insensitive' } },
				],
				active: true,
			});
		}

		const orderBy = { name_secondary: SortingType.ASCENDING };

		this.searchSubscription$ = this.organizationsService
			.findMany(
				new PaginationArgs({
					limit: this.limit,
					orderBy,
					page: 1,
					select,
					where,
				}),
			)
			.subscribe((response) => {
				const organizations: OrganizationDto[] = response.result;

				// Se o usuário for Sanus, então exibe a opção "Todas as Organizações" no cabeçalho
				if (this.userOrganizationId === AppConstants.organization.id) {
					organizations.unshift({
						id: AppConstants.organization.id,
						cnpj: '',
						isFavorite: false,
						name_secondary: 'Todas',
					} as OrganizationDto);
				}

				this.organizations = organizations;
				if (!filter) {
					this.orgSelection.setOrganizationsNames(this.organizations);
				}
				this.showOrganizationFilter = true;
			});
	}
}
